import { toast } from 'sonner';
import i18next from 'i18next';

import { useWorkflowInstanceStore } from '@/store/workflow-instance.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class WorkflowInstanceIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchInstances(params = {}) {
        const store = useWorkflowInstanceStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const { columnFilters, ...requestParams } = params;
            const response = await this.service.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const items = (response.data || []).map((it) => ({ ...it, id: it._id || it.id }));
            store.setItems(items);
            if (response.pagination && store.setPaginationData) {
                store.setPaginationData(response.pagination);
            }
            store.setUniqueValues(response.uniqueValues || {});
            return { success: true, data: items, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async getById(id) {
        const store = useWorkflowInstanceStore.getState();
        try {
            const response = await this.service.getById(id);
            store.setSelected(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async start(payload) {
        try {
            const response = await this.service.start(payload);
            toast.success(
                i18next.t('workflow:messages.startSuccess', { defaultValue: 'Workflow started' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async cancel(id, reason) {
        try {
            const response = await this.service.cancel(id, { reason });
            toast.success(
                i18next.t('workflow:messages.cancelSuccess', { defaultValue: 'Workflow cancelled' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async suspend(id, reason) {
        try {
            const response = await this.service.suspend(id, { reason });
            toast.success(
                i18next.t('workflow:messages.suspendSuccess', { defaultValue: 'Workflow suspended' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async resume(id) {
        try {
            const response = await this.service.resume(id, {});
            toast.success(
                i18next.t('workflow:messages.resumeSuccess', { defaultValue: 'Workflow in progress' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async injectApprovers(id, body) {
        try {
            const response = await this.service.injectApprovers(id, body || {});
            toast.success(
                i18next.t('workflow:messages.injectApproversSuccess', { defaultValue: 'Approvers added' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async emergencyComplete(id, body) {
        try {
            const response = await this.service.emergencyComplete(id, body || {});
            toast.success(
                i18next.t('workflow:messages.emergencyCompleteSuccess', { defaultValue: 'Workflow completed as emergency' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }
}
